import { createContext, useCallback, useContext, useMemo } from 'react';

import { AreaKey } from '../../utilities/helpers/pageVocabulary.generated';
import { useRouteArea } from '../../utilities/helpers/routeArea';

export type ModalContentPropsBase = {
  close: () => void;
};

export type ModalConfig = {
  content: React.ReactNode | ((props: ModalContentPropsBase) => React.ReactNode);
  tailwindMaxWidthClass?: string;
  isMaxHeight?: boolean
  /** The area of the page that opened the modal, which its content is judged by. Set by useGlobalModal. */
  openerArea?: AreaKey;
};

export type ModalContextType = {
  showModal: (config: ModalConfig, modalKey?: string) => void;
  hideModal: () => void;
  isOpen: boolean;
  modalKey?: string;
};

export const GlobalModalContext = createContext<ModalContextType | undefined>(undefined);

const useModalContext = () => {
  const context = useContext(GlobalModalContext);
  if (!context) throw new Error('useGlobalModal must be used inside GlobalModalProvider');
  return context;
};

export const useGlobalModal = (): ModalContextType => {
  const context = useModalContext();
  const openerArea = useRouteArea();
  const { showModal: show } = context;

  const showModal = useCallback(
    (config: ModalConfig, modalKey?: string) => show({ ...config, openerArea }, modalKey),
    [show, openerArea]
  );

  return useMemo(() => ({ ...context, showModal }), [context, showModal]);
};
